(() => {
  const SECTION_SELECTOR = '[data-cd-safe-secure]';
  const SLIDE_SELECTOR = '[data-cd-safe-slide]';
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  if (window.CDCleerHomeSafeSecure?.mount) {
    window.CDCleerHomeSafeSecure.mount(document);
    return;
  }

  class SafeSecureHighlights {
    constructor(section) {
      this.section = section;
      this.scroller = section.querySelector('[data-cd-safe-rail-clip]');
      this.rail = section.querySelector('[data-cd-safe-rail]');
      this.pagination = section.querySelector('[data-cd-safe-pagination]');
      this.playbackButton = section.querySelector('[data-cd-safe-playback]');
      this.status = section.querySelector('[data-cd-safe-status]');
      this.slides = [...section.querySelectorAll(SLIDE_SELECTOR)];
      this.cardHeaders = this.slides.map((slide) => slide.querySelector('.cd-home-safe-secure__card-header'));
      this.pageButtons = [...section.querySelectorAll('[data-cd-safe-page]')];
      this.progressBars = [...section.querySelectorAll('[data-cd-safe-progress]')];
      this.videos = [...section.querySelectorAll('.cd-home-safe-secure__card-video')];
      this.stickyAtcBars = [...document.querySelectorAll('[data-cd-sticky-atc], .t4s-sticky-atc')];

      this.activeIndex = 0;
      this.activeVideo = null;
      this.progress = 0;
      this.elapsedMs = 0;
      this.lastTickTime = null;
      this.slideDuration = Math.max(3000, Number(section.dataset.cdSafeDuration) || 6000);
      this.playbackRequested = section.dataset.cdSafeAutoplay === 'true' && !reducedMotion();
      this.pauseOnHover = section.dataset.cdSafePauseHover !== 'false';
      this.inView = true;
      this.hovered = false;
      this.interacting = false;
      this.pointerDown = false;
      this.pointerId = null;
      this.pointerType = '';
      this.startX = 0;
      this.lastX = 0;
      this.startTime = 0;
      this.startScrollLeft = 0;
      this.startIndex = 0;
      this.pointerMoved = false;
      this.raf = null;
      this.tickRaf = null;
      this.scrollEndTimer = null;
      this.resizeTimer = null;
      this.scrollAnimation = null;
      this.stickyAtcObserver = null;
      this.previousSnapType = '';
      this.ignoreScrollUntil = 0;

      if (!this.scroller || !this.rail || !this.slides.length) return;

      this.onScroll = this.onScroll.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onPaginationClick = this.onPaginationClick.bind(this);
      this.onPlaybackClick = this.onPlaybackClick.bind(this);
      this.onCardClick = this.onCardClick.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onVideoPlayState = this.onVideoPlayState.bind(this);
      this.onVideoEnded = this.onVideoEnded.bind(this);
      this.syncStickyBarOffset = this.syncStickyBarOffset.bind(this);

      this.prepare();
      this.updateCopyVisibility();
      this.bindEvents();

      window.requestAnimationFrame(() => {
        this.jumpTo(0);
        this.setActive(0, { reset: true, announce: false });
        this.updateCopyVisibility();
        if (this.playbackRequested) this.resumePlayback();
      });
    }

    prepare() {
      this.section.classList.toggle('has-single-slide', this.slides.length < 2);
      this.section.classList.toggle('has-video', this.videos.length > 0);

      this.scroller.setAttribute('tabindex', '0');
      this.scroller.setAttribute('role', 'region');
      this.scroller.setAttribute('aria-roledescription', 'carousel');
      this.scroller.setAttribute('aria-label', 'Safe and Secure highlights');

      this.slides.forEach((slide, index) => {
        slide.dataset.cdSafeIndex = String(index);
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', `${index + 1} of ${this.slides.length}`);
        slide.setAttribute('draggable', 'false');
      });

      this.rail.querySelectorAll('img').forEach((image) => {
        image.setAttribute('draggable', 'false');
        image.addEventListener('dragstart', (event) => event.preventDefault());
      });

      this.videos.forEach((video) => {
        video.playsInline = true;
        video.muted = true;
        video.addEventListener('play', this.onVideoPlayState);
        video.addEventListener('pause', this.onVideoPlayState);
        video.addEventListener('loadedmetadata', this.onVideoPlayState);
        video.addEventListener('timeupdate', this.onVideoPlayState);
        video.addEventListener('ended', this.onVideoEnded);
      });

      this.syncStickyBarOffset();
      if (this.stickyAtcBars.length && 'MutationObserver' in window) {
        this.stickyAtcObserver = new MutationObserver(this.syncStickyBarOffset);
        this.stickyAtcBars.forEach((bar) => {
          this.stickyAtcObserver.observe(bar, {
            attributes: true,
            attributeFilter: ['class', 'hidden', 'style']
          });
        });
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          const entry = entries[0];
          this.inView = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.2);
          if (this.isSuspended()) this.pauseActiveVideo();
          else if (this.playbackRequested) this.resumePlayback();
        }, { threshold: [0, 0.2, 0.5] });
        this.observer.observe(this.section);
      }
    }

    bindEvents() {
      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      this.scroller.addEventListener('wheel', this.onWheel, { passive: true });
      this.scroller.addEventListener('pointerdown', this.onPointerDown);
      window.addEventListener('pointermove', this.onPointerMove, { passive: false });
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
      this.scroller.addEventListener('keydown', this.onKeyDown);
      this.rail.addEventListener('click', this.onCardClick);
      this.pagination?.addEventListener('click', this.onPaginationClick);
      this.playbackButton?.addEventListener('click', this.onPlaybackClick);
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('orientationchange', this.onResize, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (this.pauseOnHover) {
        this.section.addEventListener('mouseenter', this.onMouseEnter);
        this.section.addEventListener('mouseleave', this.onMouseLeave);
      }
    }

    isSuspended() {
      return document.hidden || !this.inView || this.hovered || this.interacting;
    }

    syncStickyBarOffset() {
      const visibleHeight = this.stickyAtcBars.reduce((largest, bar) => {
        if (bar.hidden) return largest;
        if (bar.matches('[data-cd-sticky-atc]') && !bar.classList.contains('is-visible')) return largest;
        if (bar.matches('.t4s-sticky-atc') && !bar.classList.contains('is--shown')) return largest;
        const styles = window.getComputedStyle(bar);
        if (styles.display === 'none' || styles.visibility === 'hidden') return largest;
        return Math.max(largest, bar.getBoundingClientRect().height);
      }, 0);

      this.section.style.setProperty(
        '--cd-safe-sticky-bar-offset',
        `${Math.ceil(visibleHeight)}px`
      );
    }

    getTargetLeft(index) {
      const slide = this.slides[index];
      if (!slide) return 0;
      const maxScroll = Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
      if (index === 0) return 0;
      if (index === this.slides.length - 1) return maxScroll;
      return clamp(
        slide.offsetLeft - ((this.scroller.clientWidth - slide.offsetWidth) / 2),
        0,
        maxScroll
      );
    }

    getNearestIndex() {
      const maxScroll = Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
      if (this.scroller.scrollLeft <= 2) return 0;
      if (maxScroll - this.scroller.scrollLeft <= 2) return this.slides.length - 1;

      const center = this.scroller.scrollLeft + (this.scroller.clientWidth / 2);
      let nearest = 0;
      let nearestDistance = Infinity;
      this.slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + (slide.offsetWidth / 2);
        const distance = Math.abs(slideCenter - center);
        if (distance < nearestDistance) {
          nearest = index;
          nearestDistance = distance;
        }
      });
      return nearest;
    }

    getCopyFadeDistance(index) {
      const currentTarget = this.getTargetLeft(index);
      const previousTarget = index > 0 ? this.getTargetLeft(index - 1) : null;
      const nextTarget = index < this.slides.length - 1 ? this.getTargetLeft(index + 1) : null;
      const distances = [previousTarget, nextTarget]
        .filter((target) => Number.isFinite(target))
        .map((target) => Math.abs(target - currentTarget))
        .filter((distance) => distance > 0);
      const nearestStep = distances.length ? Math.min(...distances) : this.slides[index]?.offsetWidth || 1;
      return Math.max(120, nearestStep * 0.72);
    }

    updateCopyVisibility() {
      if (reducedMotion()) return;
      const currentScroll = this.scroller.scrollLeft;

      this.cardHeaders.forEach((header, index) => {
        if (!header) return;
        const distance = Math.abs(currentScroll - this.getTargetLeft(index));
        const rawVisibility = 1 - clamp(distance / this.getCopyFadeDistance(index), 0, 1);
        const visibility = rawVisibility * rawVisibility * (3 - (2 * rawVisibility));
        const shift = (1 - visibility) * 14;
        header.style.setProperty('--cd-safe-copy-opacity', visibility.toFixed(4));
        header.style.setProperty('--cd-safe-copy-shift', `${shift.toFixed(2)}px`);
      });
    }

    scheduleCopyVisibility() {
      if (this.raf) return;
      this.raf = window.requestAnimationFrame(() => {
        this.raf = null;
        this.updateCopyVisibility();
      });
    }

    cancelScrollAnimation() {
      if (!this.scrollAnimation) return;
      window.cancelAnimationFrame(this.scrollAnimation.raf);
      this.scrollAnimation = null;
      this.scroller.style.scrollSnapType = this.previousSnapType;
    }

    jumpTo(index) {
      this.cancelScrollAnimation();
      const previousBehavior = this.scroller.style.scrollBehavior;
      const previousSnap = this.scroller.style.scrollSnapType;
      this.scroller.style.scrollBehavior = 'auto';
      this.scroller.style.scrollSnapType = 'none';
      this.ignoreScrollUntil = performance.now() + 80;
      this.scroller.scrollLeft = this.getTargetLeft(index);
      this.scroller.getBoundingClientRect();
      this.updateCopyVisibility();
      this.scroller.style.scrollBehavior = previousBehavior;
      this.scroller.style.scrollSnapType = previousSnap;
    }

    animateTo(index) {
      const target = this.getTargetLeft(index);
      const start = this.scroller.scrollLeft;
      const distance = target - start;

      this.cancelScrollAnimation();
      if (reducedMotion() || Math.abs(distance) < 1) {
        this.jumpTo(index);
        return Promise.resolve();
      }

      this.previousSnapType = this.scroller.style.scrollSnapType;
      this.scroller.style.scrollSnapType = 'none';
      const duration = clamp(480 + Math.abs(distance) * 0.18, 520, 760);

      return new Promise((resolve) => {
        const started = performance.now();
        const animation = { raf: null };
        this.scrollAnimation = animation;

        const frame = (now) => {
          if (this.scrollAnimation !== animation) return resolve();
          const time = clamp((now - started) / duration, 0, 1);
          const eased = 1 - Math.pow(1 - time, 4);
          this.scroller.scrollLeft = start + distance * eased;
          this.updateCopyVisibility();

          if (time < 1) {
            animation.raf = window.requestAnimationFrame(frame);
            return;
          }

          this.scroller.scrollLeft = target;
          this.updateCopyVisibility();
          this.ignoreScrollUntil = performance.now() + 100;
          this.scrollAnimation = null;
          this.scroller.style.scrollSnapType = this.previousSnapType;
          resolve();
        };

        animation.raf = window.requestAnimationFrame(frame);
      });
    }

    async goTo(index, options = {}) {
      const targetIndex = clamp(index, 0, this.slides.length - 1);
      const changed = targetIndex !== this.activeIndex;
      if (changed) this.setActive(targetIndex, { reset: true, announce: false });
      if (options.animate === false) this.jumpTo(targetIndex);
      else await this.animateTo(targetIndex);
      this.setActive(targetIndex, { reset: false, announce: options.announce !== false });
      this.interacting = false;
      if (this.playbackRequested && !this.isSuspended()) this.resumePlayback();
    }

    setActive(index, { reset = false, announce = false } = {}) {
      const nextIndex = clamp(index, 0, this.slides.length - 1);
      const nextSlide = this.slides[nextIndex];
      const nextVideo = nextSlide?.querySelector('.cd-home-safe-secure__card-video') || null;
      const changed = nextIndex !== this.activeIndex || nextVideo !== this.activeVideo;

      if (changed && this.activeVideo && this.activeVideo !== nextVideo) this.activeVideo.pause();

      this.activeIndex = nextIndex;
      this.activeVideo = nextVideo;
      this.slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === nextIndex);
        slide.setAttribute('aria-hidden', slideIndex === nextIndex ? 'false' : 'true');
      });
      this.pageButtons.forEach((button, buttonIndex) => {
        const active = buttonIndex === nextIndex;
        button.classList.toggle('is-active', active);
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      this.section.dataset.cdSafeActiveSlide = String(nextIndex);

      if (reset || changed) {
        this.elapsedMs = 0;
        this.lastTickTime = null;
        this.progress = 0;
        if (nextVideo && Number.isFinite(nextVideo.duration)) nextVideo.currentTime = 0;
      }

      this.renderProgress();
      if (announce) this.announceActiveSlide();
      if (this.playbackRequested && !this.isSuspended()) this.playActiveVideo();
      this.syncPlaybackButton();
    }

    announceActiveSlide() {
      if (!this.status) return;
      const slide = this.slides[this.activeIndex];
      const label = slide?.querySelector('.cd-home-safe-secure__card-top-text, .cd-home-safe-secure__card-title')?.textContent?.trim();
      this.status.textContent = `Slide ${this.activeIndex + 1} of ${this.slides.length}${label ? `: ${label}` : ''}`;
    }

    renderProgress() {
      this.progressBars.forEach((bar, index) => {
        bar.style.setProperty('--cd-safe-progress', index === this.activeIndex ? String(clamp(this.progress, 0, 1)) : '0');
      });
    }

    syncPlaybackButton() {
      if (!this.playbackButton) return;
      this.playbackButton.dataset.playing = this.playbackRequested ? 'true' : 'false';
      this.playbackButton.setAttribute('aria-pressed', this.playbackRequested ? 'true' : 'false');
      this.playbackButton.setAttribute('aria-label', this.playbackRequested ? 'Pause carousel' : 'Play carousel');
    }

    playActiveVideo() {
      if (!this.activeVideo || !this.activeVideo.paused || this.activeVideo.ended) return;
      const playRequest = this.activeVideo.play();
      if (playRequest?.catch) playRequest.catch(() => this.pausePlayback());
    }

    pauseActiveVideo() {
      if (this.activeVideo && !this.activeVideo.paused) this.activeVideo.pause();
    }

    pauseInactiveVideos() {
      this.videos.forEach((video) => {
        if (video !== this.activeVideo && !video.paused) video.pause();
      });
    }

    resumePlayback() {
      this.playbackRequested = true;
      this.pauseInactiveVideos();
      if (!this.isSuspended()) this.playActiveVideo();
      this.lastTickTime = null;
      this.syncPlaybackButton();
      this.scheduleTick();
    }

    pausePlayback() {
      this.playbackRequested = false;
      this.lastTickTime = null;
      this.pauseActiveVideo();
      if (this.tickRaf) window.cancelAnimationFrame(this.tickRaf);
      this.tickRaf = null;
      this.syncPlaybackButton();
    }

    scheduleTick() {
      if (!this.playbackRequested || this.tickRaf) return;
      this.tickRaf = window.requestAnimationFrame((time) => this.tick(time));
    }

    tick(time) {
      this.tickRaf = null;
      if (!this.playbackRequested) return;

      if (this.isSuspended()) {
        this.lastTickTime = null;
        this.scheduleTick();
        return;
      }

      if (this.activeVideo) {
        const duration = this.activeVideo.duration;
        if (Number.isFinite(duration) && duration > 0) {
          this.progress = clamp(this.activeVideo.currentTime / duration, 0, 1);
          this.renderProgress();
        }
      } else {
        if (this.lastTickTime === null) this.lastTickTime = time;
        const delta = clamp(time - this.lastTickTime, 0, 100);
        this.lastTickTime = time;
        this.elapsedMs += delta;
        this.progress = clamp(this.elapsedMs / this.slideDuration, 0, 1);
        this.renderProgress();

        if (this.progress >= 1) {
          this.advancePlayback();
          return;
        }
      }

      this.scheduleTick();
    }

    advancePlayback() {
      if (this.activeIndex >= this.slides.length - 1) {
        this.progress = 1;
        this.renderProgress();
        this.pausePlayback();
        return;
      }
      this.goTo(this.activeIndex + 1, { animate: true, announce: true });
    }

    onScroll() {
      this.scheduleCopyVisibility();
      if (this.scrollAnimation || performance.now() < this.ignoreScrollUntil) return;
      this.interacting = true;
      this.pauseActiveVideo();
      window.clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = window.setTimeout(() => {
        if (this.pointerDown) return;
        const nearest = this.getNearestIndex();
        const changed = nearest !== this.activeIndex;
        if (changed) this.setActive(nearest, { reset: true, announce: false });
        this.goTo(nearest, { animate: true, announce: changed });
      }, 140);
    }

    onWheel() {
      this.cancelScrollAnimation();
      this.interacting = true;
      this.pauseActiveVideo();
    }

    onPointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      if (event.target.closest?.('button, a, input, select, textarea, summary')) return;

      this.cancelScrollAnimation();
      window.clearTimeout(this.scrollEndTimer);
      this.pointerDown = true;
      this.interacting = true;
      this.pointerId = event.pointerId;
      this.pointerType = event.pointerType || 'mouse';
      this.startX = event.clientX;
      this.lastX = event.clientX;
      this.startTime = performance.now();
      this.startScrollLeft = this.scroller.scrollLeft;
      this.startIndex = this.activeIndex;
      this.pointerMoved = false;
      this.pauseActiveVideo();

      if (this.pointerType === 'mouse') {
        event.preventDefault();
        this.previousSnapType = this.scroller.style.scrollSnapType;
        this.scroller.style.scrollSnapType = 'none';
        this.scroller.classList.add('is-dragging');
        try {
          this.scroller.setPointerCapture(event.pointerId);
        } catch (error) {
          // Window listeners keep the gesture working if capture is unavailable.
        }
      }
    }

    onPointerMove(event) {
      if (!this.pointerDown || event.pointerId !== this.pointerId) return;
      const delta = event.clientX - this.startX;
      this.lastX = event.clientX;
      if (Math.abs(delta) > 5) this.pointerMoved = true;

      if (this.pointerType === 'mouse') {
        event.preventDefault();
        this.scroller.scrollLeft = this.startScrollLeft - delta;
      }
    }

    onPointerUp(event) {
      if (!this.pointerDown || (event.pointerId !== undefined && event.pointerId !== this.pointerId)) return;

      const pointerType = this.pointerType;
      const delta = this.lastX - this.startX;
      const elapsed = Math.max(1, performance.now() - this.startTime);
      const velocity = delta / elapsed;
      const pointerId = this.pointerId;

      this.pointerDown = false;
      this.pointerId = null;
      this.pointerType = '';
      window.clearTimeout(this.scrollEndTimer);

      if (pointerType === 'mouse') {
        this.scroller.classList.remove('is-dragging');
        this.scroller.style.scrollSnapType = this.previousSnapType;
        try {
          this.scroller.releasePointerCapture(pointerId);
        } catch (error) {
          // The browser may have already released capture.
        }

        let target = this.getNearestIndex();
        if (Math.abs(delta) > 52 || Math.abs(velocity) > 0.45) {
          target = delta < 0 ? this.startIndex + 1 : this.startIndex - 1;
        }
        target = clamp(target, 0, this.slides.length - 1);
        const changed = target !== this.activeIndex;
        if (changed) this.setActive(target, { reset: true, announce: false });
        this.goTo(target, { animate: true, announce: changed });
        return;
      }

      window.clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = window.setTimeout(() => {
        const target = this.getNearestIndex();
        const changed = target !== this.activeIndex;
        if (changed) this.setActive(target, { reset: true, announce: false });
        this.goTo(target, { animate: true, announce: changed });
      }, 120);
    }

    onCardClick(event) {
      if (this.pointerMoved) {
        this.pointerMoved = false;
        return;
      }
      const slide = event.target.closest?.(SLIDE_SELECTOR);
      if (!slide) return;
      const index = Number(slide.dataset.cdSafeIndex);
      if (Number.isInteger(index) && index !== this.activeIndex) {
        this.goTo(index, { animate: true, announce: true });
      }
    }

    onKeyDown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.activeIndex + 1, { animate: true, announce: true });
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.activeIndex - 1, { animate: true, announce: true });
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0, { animate: true, announce: true });
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.slides.length - 1, { animate: true, announce: true });
      }
    }

    onPaginationClick(event) {
      const button = event.target.closest?.('[data-cd-safe-page]');
      if (!button || !this.pagination?.contains(button)) return;
      const index = Number(button.dataset.cdSafePage);
      if (!Number.isInteger(index)) return;
      this.goTo(index, { animate: true, announce: true });
    }

    onPlaybackClick() {
      if (this.playbackRequested) {
        this.pausePlayback();
        return;
      }

      if (this.activeIndex === this.slides.length - 1 && this.progress >= 0.999) {
        this.playbackRequested = true;
        this.goTo(0, { animate: true, announce: true });
      } else {
        this.resumePlayback();
      }
    }

    onVideoPlayState(event) {
      if (event.target !== this.activeVideo) return;
      if (Number.isFinite(this.activeVideo.duration) && this.activeVideo.duration > 0) {
        this.progress = clamp(this.activeVideo.currentTime / this.activeVideo.duration, 0, 1);
        this.renderProgress();
      }
    }

    onVideoEnded(event) {
      if (event.target !== this.activeVideo) return;
      this.progress = 1;
      this.renderProgress();
      if (this.playbackRequested) this.advancePlayback();
    }

    onResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        this.syncStickyBarOffset();
        this.jumpTo(this.activeIndex);
      }, 120);
    }

    onVisibilityChange() {
      if (document.hidden) this.pauseActiveVideo();
      else if (this.playbackRequested && !this.isSuspended()) this.resumePlayback();
    }

    onMouseEnter() {
      this.hovered = true;
      this.pauseActiveVideo();
    }

    onMouseLeave() {
      this.hovered = false;
      if (this.playbackRequested && !this.isSuspended()) this.resumePlayback();
    }

    destroy() {
      if (this.raf) window.cancelAnimationFrame(this.raf);
      if (this.tickRaf) window.cancelAnimationFrame(this.tickRaf);
      window.clearTimeout(this.scrollEndTimer);
      window.clearTimeout(this.resizeTimer);
      this.cancelScrollAnimation();
      this.pauseActiveVideo();
      this.observer?.disconnect();
      this.stickyAtcObserver?.disconnect();

      this.scroller?.removeEventListener('scroll', this.onScroll);
      this.scroller?.removeEventListener('wheel', this.onWheel);
      this.scroller?.removeEventListener('pointerdown', this.onPointerDown);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      this.scroller?.removeEventListener('keydown', this.onKeyDown);
      this.rail?.removeEventListener('click', this.onCardClick);
      this.pagination?.removeEventListener('click', this.onPaginationClick);
      this.playbackButton?.removeEventListener('click', this.onPlaybackClick);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.section.removeEventListener('mouseenter', this.onMouseEnter);
      this.section.removeEventListener('mouseleave', this.onMouseLeave);
      this.section.style.removeProperty('--cd-safe-sticky-bar-offset');

      this.cardHeaders.forEach((header) => {
        header?.style.removeProperty('--cd-safe-copy-opacity');
        header?.style.removeProperty('--cd-safe-copy-shift');
      });

      this.videos.forEach((video) => {
        video.removeEventListener('play', this.onVideoPlayState);
        video.removeEventListener('pause', this.onVideoPlayState);
        video.removeEventListener('loadedmetadata', this.onVideoPlayState);
        video.removeEventListener('timeupdate', this.onVideoPlayState);
        video.removeEventListener('ended', this.onVideoEnded);
      });

      this.section.removeAttribute('data-cd-safe-ready');
    }
  }

  const instances = new WeakMap();

  function initSection(section) {
    if (!section || instances.has(section)) return;
    const instance = new SafeSecureHighlights(section);
    instances.set(section, instance);
    section.setAttribute('data-cd-safe-ready', 'true');
  }

  function destroySection(section) {
    const instance = instances.get(section);
    if (!instance) return;
    instance.destroy();
    instances.delete(section);
  }

  function initAll(root = document) {
    root.querySelectorAll?.(SECTION_SELECTOR).forEach(initSection);
    if (root.matches?.(SECTION_SELECTOR)) initSection(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target?.querySelector?.(SECTION_SELECTOR)
      || (event.target?.matches?.(SECTION_SELECTOR) ? event.target : null);
    if (section) destroySection(section);
  });

  window.CDCleerHomeSafeSecure = {
    mount: initAll,
    destroy: destroySection
  };
})();
